import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ActivityIndicator,
} from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import { AUDIO_LANGUAGES, Colors, SELECT_STYLES } from "@/constants";
import { useEffect, useState } from "react";
import { useUserSlice } from "@/models/useUserSlice";
import Icon from "./Icon";
import { useRecorder } from "@/models/useRecorder";
import {
  formatTimeToHHMMSS,
  getBasePath,
  getFormattedDate,
} from "@/models/utils";
import { useRouter } from "@/_router";
import { getPlatform } from "@/models/platform";
import { useLiveRecorderIOS } from "@/models/useLiveRecorderIOS";
import { useLiveRecorderWeb } from "@/models/useLiveRecorderWeb";

const AlertWidgetCreateNoteVoiceRecording = () => {
  const visible = useUISlice(
    (state) => state.alertWidget === "voice_recording"
  );
  const setVisible = useUISlice((state) => state.setAlertWidget);
  const router = useRouter();
  const width = useUISlice((state) => state.device.width);
  const transcriptionLanguage = useUserSlice(
    (state) => state.userData?.preferences?.transcriptionLanguage
  );
  const [language, setLanguage] = useState<string>(
    transcriptionLanguage || AUDIO_LANGUAGES[0].value
  );
  const { t } = useTranslation();
  const isLiveRecorder = ["ios", "web"].includes(getPlatform());
  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const folders = useUserSlice((state) => {
    if (state?.folders?.length === 0) return undefined;
    return [
      { id: undefined, name: t("alertWidgetCreateNoteMediaImport.allNotes") },
      ...state.folders,
    ];
  });

  const {
    status,
    elapsedTime,
    startRecording,
    stopRecording,
    pauseOrPlay,
    cancelRecording,
  } = {
    ios: useLiveRecorderIOS(),
    android: useRecorder(),
    web: useLiveRecorderWeb(),
  }[getPlatform()];

  useEffect(() => {
    if (visible) {
      onStartRecording();
    }
  }, [visible]);

  if (!visible) return null;

  const onStartRecording = async () => {
    useUserSlice
      .getState()
      .note()
      .create(
        {
          type: "voice_recording",
          name: t("alertWidgetCreateNoteVoiceRecording.recordingName", {
            date: getFormattedDate(),
          }),
          transcriptionLanguage: language,
          status: "upload_started",
        },
        undefined,
        async (note) => {
          useUISlice.getState().setLiveRecording({ noteId: note.id });
          await startRecording();
        }
      );
  };

  const onStopRecording = async () => {
    setVisible(undefined);
    const noteId = useUISlice.getState().liveRecording?.noteId;

    if (folderId) {
      useUserSlice.getState().folder().addNote(folderId, noteId);
    }

    if (isLiveRecorder) {
      // use live recorder
      await stopRecording(async () => {
        router.push(`${getBasePath()}/note?id=${noteId}`);
        useUISlice.getState().setAlertModal(undefined);
        useUISlice.getState().setLiveRecording(undefined);
      });
    } else {
      // use recorder
      await (stopRecording as ReturnType<typeof useRecorder>["stopRecording"])(
        async (file) => {
          useUISlice
            .getState()
            .setNoteScreenData({ upload_started: { file, language } });
          router.push(`${getBasePath()}/note?id=${noteId}`);
          useUISlice.getState().setAlertModal(undefined);
        }
      );
    }
  };

  const onDeleteRecording = () => {
    useUISlice.getState().setAlertModal({
      title: t("alertWidgetCreateNoteVoiceRecording.deleteRecordingTitle"),
      subtitle: t(
        "alertWidgetCreateNoteVoiceRecording.deleteRecordingSubtitle"
      ),
      cancelText: t("alertWidgetCreateNoteVoiceRecording.cancel"),
      button: {
        text: t("alertWidgetCreateNoteVoiceRecording.deleteButton"),
        destructive: true,
        onPress: () => {
          setVisible(undefined);
          useUISlice.getState().setAlertModal(undefined);

          cancelRecording(async () => {
            const noteId = useUISlice.getState().liveRecording?.noteId;
            useUserSlice.getState().note().delete(noteId, undefined, true);
            useUISlice.getState().setLiveRecording(undefined);
          });
        },
      },
    });
  };

  return (
    <ModalContainer
      shouldPersistBackdropClick
      title={t("alertWidgetCreateNoteVoiceRecording.title")}
      onDismiss={onDeleteRecording}
      hideCancel
      modalWidth={Math.min(width, 600)}
    >
      <View style={styles.spacer} />

      <select
        style={SELECT_STYLES}
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
      >
        {AUDIO_LANGUAGES.map((language) => (
          <option key={language.value} value={language.value}>
            {language.label}
          </option>
        ))}
      </select>

      <View style={styles.spacer} />

      {folders && (
        <>
          <View style={styles.spacer} />
          <Text style={styles.label}>
            {t("alertWidgetCreateNoteMediaImport.folder")}
          </Text>
          <select
            style={SELECT_STYLES}
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
          >
            {folders.map((folder, index) => (
              <option key={index} value={folder.id}>
                {`📂 ${folder.name}`}
              </option>
            ))}
          </select>
        </>
      )}

      <View style={styles.largerSpacer} />

      {["recording", "paused"].includes(status) && (
        <View style={{ flexDirection: "row", gap: ms(8) }}>
          <Pressable
            onPress={pauseOrPlay}
            style={{
              ...styles.button,
              minHeight: ms(60),
              backgroundColor: Colors.text + "10",
            }}
          >
            <Icon
              icon={status === "recording" ? "pause" : "play"}
              style={{ width: ms(24), height: ms(24), tintColor: Colors.text }}
            />
          </Pressable>

          <View
            style={{
              ...styles.button,
              backgroundColor: "transparent",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: ms(4),
            }}
          >
            <Icon
              icon="mic"
              style={{
                width: ms(16),
                height: ms(16),
                tintColor: Colors.softRed,
              }}
            />
            <Text
              style={{
                ...styles.buttonText,
                color: Colors.softRed,
                fontSize: ms(14),
                fontWeight: "500",
              }}
            >
              {formatTimeToHHMMSS(elapsedTime)}
            </Text>
          </View>

          <Pressable
            onPress={onStopRecording}
            style={{
              ...styles.button,
              minHeight: ms(60),
              backgroundColor: Colors.softRed,
            }}
          >
            <Text style={{ ...styles.buttonText, color: Colors.white }}>
              {t("alertWidgetCreateNoteVoiceRecording.end")}
            </Text>
          </Pressable>
        </View>
      )}

      {!["recording", "paused"].includes(status) && (
        <>
          <View
            style={{
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
              height: ms(100),
            }}
          >
            <ActivityIndicator size="large" color={Colors.text} />
          </View>
        </>
      )}

      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  spacer: {
    height: ms(16),
  },
  largerSpacer: {
    height: ms(48),
  },
  label: {
    fontSize: ms(16),
    fontWeight: "400",
    color: Colors.text,
    marginBottom: ms(8),
  },
  negativeMargin: {
    marginBottom: -ms(16),
  },
  button: {
    height: ms(60),
    flex: 1,
    backgroundColor: "#ffdedc",
    borderRadius: ms(60),
    justifyContent: "center",
    alignItems: "center",
  },
  buttonText: {
    fontWeight: "600",
    fontSize: ms(16),
    color: Colors.softRed,
  },
});

export default AlertWidgetCreateNoteVoiceRecording;
